





































































































































import { computed, defineComponent, nextTick, PropType, ref, toRefs } from '@vue/composition-api';
import { AdgroupFormData, AttributionWindowType, ExternalType } from '@tt4b/ttam-enums';
import { i18n } from '@tt4b/ttam-i18n';
import { trackEvent } from '@tt4b/ttam-utils';
import QuestionTooltip from 'src/components/common/question-tooltip/index.vue';

const { ONE, SEVEN, FOURTEEN, TWENTY_EIGHT } = AttributionWindowType;

export default defineComponent({
  name: 'AttributionWindowSetting',

  components: {
    QuestionTooltip,
  },

  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    isSupportEngagedView: {
      type: Boolean,
      default: false,
    },
    adgroupData: {
      type: Object as PropType<AdgroupFormData>,
      required: true,
    },
    clickList: {
      type: Array,
      default: () => [ONE, SEVEN, FOURTEEN, TWENTY_EIGHT],
    },
    viewList: {
      type: Array,
      default: () => [ONE, SEVEN],
    },
    engagedViewList: {
      type: Array,
      default: () => [ONE, SEVEN],
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
  },

  setup(props, { emit }) {
    const popoverNode = ref();
    const referenceNode = ref();
    const visible = ref(false);
    const isExpendAllOptions = ref();
    const { isSupportEngagedView, adgroupData, isEdit } = toRefs(props);
    const _attributionWindowView = computed({
      get: () => adgroupData.value.attributionWindowView,
      set: val => emit('change', { attributionWindowView: val }),
    });

    const _attributionWindowEngagedView = computed({
      get: () => {
        // 编辑场景下，有CTA、无EVTA，使用CTA回显
        if (isEdit.value && adgroupData.value.attributionFlag && !adgroupData.value.attributionWindowEngagedView) {
          return adgroupData.value.attributionWindowClick;
        }
        return adgroupData.value.attributionWindowEngagedView;
      },
      set: val => emit('change', { attributionWindowEngagedView: val }),
    });

    const _attributionWindowClick = computed({
      get: () => adgroupData.value.attributionWindowClick,
      set: val => emit('change', { attributionWindowClick: val }),
    });
    const emptyValueArr = [AttributionWindowType.NONE, AttributionWindowType.UNSET, undefined];

    const getLabel = (value: number, labelKey: string) =>
      emptyValueArr.includes(value) ? '' : i18n.t(labelKey, { num: value });

    const getAttributionWindowLabelWithEVTA = () => {
      // 请不要随意改动数组元素的相对位置
      const options = [
        {
          value: _attributionWindowClick.value,
          labelKey: 'adgroup_attribution_window_click_option_label_plural',
        },
        {
          value: _attributionWindowEngagedView.value,
          labelKey: 'adgroup_attribution_window_engaged_option_label_plural',
        },
        {
          value: _attributionWindowView.value,
          labelKey: 'adgroup_attribution_window_view_option_label_plural',
        },
      ];

      // 过滤空值
      const labels = options
        .filter(opt => !emptyValueArr.includes(opt.value))
        .map(opt => getLabel(opt.value, opt.labelKey));

      if (!labels.length) {
        return '-';
      }
      // 请不要随意改动数组元素的相对位置
      const content = [
        labels[0],
        i18n.t('adgroup_attribution_window_combine_two_options_label', { label1: labels[0], label2: labels[1] }),
        i18n.t('adgroup_attribution_window_combine_three_options_label', {
          label1: labels[0],
          label2: labels[1],
          label3: labels[2],
        }),
      ];

      // 根据过滤空值后的数组长度，选择组合的starling key
      return labels.length <= content.length ? content[labels.length - 1] : '';
    };
    const label = computed(() => {
      if (isSupportEngagedView.value) {
        return getAttributionWindowLabelWithEVTA();
      }
      const clickLabel = getLabel(_attributionWindowClick.value, 'adgroup_attribution_window_click_option_label');

      const viewLabel = getLabel(_attributionWindowView.value, 'adgroup_attribution_window_view_option_label');

      if (!clickLabel && !viewLabel) {
        return '-';
      }
      if (!viewLabel) {
        return clickLabel;
      }
      if (!clickLabel) {
        return viewLabel;
      }

      return i18n.t('adgroup_attribution_window_setting_label', { click: clickLabel, view: viewLabel });
    });

    const isAppDefaultOptions = computed(() => props.adgroupData.externalType === ExternalType.ANDRIOD_APP);

    const clickOptions = computed(() => {
      if (isExpendAllOptions.value) {
        return props.clickList;
      }

      return [ONE, SEVEN];
    });

    const viewOptions = computed(() => (isExpendAllOptions.value ? props.viewList : [AttributionWindowType.ONE]));
    const engagedViewOptions = computed(() =>
      isExpendAllOptions.value ? props.engagedViewList : [AttributionWindowType.ONE, AttributionWindowType.SEVEN],
    );

    const isShowOptionMoreBtn = computed(() => props.viewList.length > 1 || props.clickList.length > 2);

    const eventParam = computed(() => {
      const { externalAction, externalType, optimizeGoal, adRefPixelId, adRefAppId, inventoryFlow } = props.adgroupData;

      return {
        optimization_type: externalType,
        optimization_goal: optimizeGoal,
        pixel_id: adRefPixelId,
        optimization_event: externalAction,
        app_id: adRefAppId,
        placement: inventoryFlow.join(','),
      };
    });

    const panelUnfoldEvent = () => {
      trackEvent('ttam_adgroup_attribution_window_unfold_click', eventParam.value);
    };

    const close = (target: Event) => {
      const isInput = referenceNode.value === target?.target;
      if (isInput) {
        return;
      }
      panelUnfoldEvent();
      visible.value = false;
      window.removeEventListener('click', close);
    };

    const adjustPanelStatus = () => {
      const { attributionWindowView, attributionWindowClick } = props.adgroupData;
      if (
        (attributionWindowView &&
          !(
            viewOptions.value.includes(attributionWindowView) || attributionWindowView === AttributionWindowType.NONE
          )) ||
        (attributionWindowClick && !clickOptions.value.includes(attributionWindowClick))
      ) {
        isExpendAllOptions.value = true;
      }
    };

    const changePopoverStatus = () => {
      if (props.disabled) {
        return;
      }

      visible.value = !visible.value;

      if (visible.value) {
        adjustPanelStatus();
        window.addEventListener('click', close);
      } else {
        panelUnfoldEvent();
        window.removeEventListener('click', close);
      }
    };

    function changeExpendsStatus() {
      isExpendAllOptions.value = !isExpendAllOptions.value;
      nextTick(() => popoverNode.value?.updatePopper());
    }

    return {
      _attributionWindowEngagedView,
      _attributionWindowView,
      _attributionWindowClick,
      popoverNode,
      referenceNode,
      AttributionWindowType,
      label,
      visible,
      isExpendAllOptions,
      isAppDefaultOptions,
      isShowOptionMoreBtn,
      clickOptions,
      viewOptions,
      engagedViewOptions,
      close,
      changePopoverStatus,
      changeExpendsStatus,
    };
  },
});
